import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Tabs, Tab, Card } from 'react-bootstrap';
import axios from 'axios';
import { PayPalButtons } from '@paypal/react-paypal-js';
import CryptoTab from '../../CryptoTab'; // Import your CryptoTab component
import { USER_ENDPOINTS, SUBSCRIPTION_ENDPOINTS, CONTENT_ENDPOINTS } from '../../../utils/constants';
import useModalHistory from '../../../hooks/useModalHistory';
import PaymentStatusChecker from '../../../services/PaymentStatusChecker';
import PendingPaymentIndicator from '../../PendingPaymentIndicator';

const PaymentModal = ({
  show,
  onHide,
  token,
  selectedPlan,
  userDetails,
  showAlert,
  onSuccess,
  accountDetails
}) => {
  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('paypal');
  const [paystackButtonDisabled, setPaystackButtonDisabled] = useState(false);
  const [country, setCountry] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [currentPlanDetails, setCurrentPlanDetails] = useState(null);

  const [pendingPaymentId, setPendingPaymentId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentStatusData, setPaymentStatusData] = useState(null);
  const [paymentChecker, setPaymentChecker] = useState(null);

  useModalHistory(show, onHide, 'payment-modal');

  // Hard-coded Rand prices
  const randPrices = {
    1: 100, // Basic plan price in Rand
    2: 370  // Plus plan price in Rand
  };

  // Get plan details when modal opens
  useEffect(() => {
    if (show) {
      // If a specific plan is selected (for plan change), use that
      if (selectedPlan) {
        fetchPlanDetails(selectedPlan.id);
      }
      // Otherwise use the current subscription from accountDetails
      else if (accountDetails && accountDetails.payment && accountDetails.payment.SubscriptionType) {
        fetchPlanDetails(accountDetails.payment.SubscriptionType);
      }
    }
  }, [show, selectedPlan, accountDetails]);

  useEffect(() => {
    const fetchCurrentPlanDetails = async () => {
      // Use the same property check as your other code
      const subscriptionType = accountDetails?.payment?.subscriptionType || accountDetails?.payment?.SubscriptionType;

      if (show && subscriptionType) {
        try {
          const response = await axios.post(
            SUBSCRIPTION_ENDPOINTS.GET_PLAN_BY_ID,
            { planId: subscriptionType }, // Use the resolved value
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          if (response.data.success) {
            setCurrentPlanDetails(response.data.plan);
          }
        } catch (error) {
          console.error('Error fetching current plan details:', error);
        }
      }
    };

    fetchCurrentPlanDetails();
  }, [show, accountDetails?.payment?.subscriptionType, accountDetails?.payment?.SubscriptionType, token]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setPlan(null);
      setError(null);
      setLoading(false);
      setPaystackButtonDisabled(false);
      setLocationLoading(true);

      // Clean up crypto payment tracking
      if (paymentChecker) {
        paymentChecker.stop();
        setPaymentChecker(null);
      }
      setPendingPaymentId(null);
      setPaymentStatus(null);
      setPaymentStatusData(null);
    }
  }, [show, paymentChecker]);

  useEffect(() => {
    const detectLocation = async () => {
      try {
        setLocationLoading(true);

        // Using ipapi.co for free geolocation
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        setCountry(data.country_code);

        // Set default tab based on location
        if (data.country_code === 'ZA') { // South Africa
          setActiveTab('paystack');
        } else {
          setActiveTab('paypal');
        }
      } catch (error) {
        console.error('Error detecting location:', error);
        // Default to PayPal if location detection fails
        setActiveTab('paypal');
        setCountry('UNKNOWN');
      } finally {
        setLocationLoading(false);
      }
    };

    // Only detect location when modal is shown
    if (show) {
      detectLocation();
    }
  }, [show]);

  const getLocationMessage = () => {
    if (locationLoading) return 'Detecting your location...';
    if (country === 'ZA') return 'Payment options for South Africa';
    if (country === 'UNKNOWN') return 'Payment options (location detection failed)';
    return 'International payment options';
  };

  // Fetch plan details from API
  const fetchPlanDetails = async (planId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        SUBSCRIPTION_ENDPOINTS.GET_PLAN_BY_ID,
        { planId },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setPlan(response.data.plan);
      } else {
        setError('Failed to fetch plan details');
      }
    } catch (error) {
      console.error('Error fetching plan details:', error);
      setError('Error loading plan details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate proration directly in PaymentModal
  const calculateProration = () => {
    if (!plan || !accountDetails) return null;

    // Check if this is an upgrade from Basic (1) to Plus (2)
    const currentPlan = parseInt(accountDetails?.payment?.subscriptionType || accountDetails?.payment?.SubscriptionType);
    const newPlan = parseInt(plan.id);

    // Only calculate proration for Basic to Plus upgrades
    if (currentPlan === 1 && newPlan === 2 && accountDetails?.payment?.NextPayDate) {
      const nextPaymentDate = new Date(accountDetails.payment.NextPayDate);
      const today = new Date();

      // Calculate remaining days
      const oneYearAgo = new Date(nextPaymentDate);
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      const totalDays = 365;
      const daysUsed = Math.floor((today - oneYearAgo) / (1000 * 60 * 60 * 24));
      const remainingDays = Math.max(0, totalDays - daysUsed);

      if (remainingDays > 0) {
        // USD calculations
        const currentUSDPrice = currentPlanDetails ? parseFloat(currentPlanDetails.Price) : 5.55;
        const newUSDPrice = parseFloat(plan.Price);
        const dailyUSDRate = currentUSDPrice / 365;
        const usdCredit = Math.round((dailyUSDRate * remainingDays) * 100) / 100;
        const proratedUSDAmount = Math.round(Math.max(0, newUSDPrice - usdCredit) * 100) / 100;

        // Rand calculations
        const currentRandPrice = 100; // Basic plan Rand price
        const newRandPrice = 370; // Plus plan Rand price
        const dailyRandRate = currentRandPrice / 365;
        const randCredit = Math.round((dailyRandRate * remainingDays) * 100) / 100;
        const proratedRandAmount = Math.round(Math.max(0, newRandPrice - randCredit) * 100) / 100;

        return {
          remainingDays,
          usd: {
            originalAmount: newUSDPrice,
            proratedAmount: proratedUSDAmount,
            credit: usdCredit
          },
          rand: {
            originalAmount: newRandPrice,
            proratedAmount: proratedRandAmount,
            credit: randCredit
          }
        };
      }
    }

    return null;
  };

  // Get the correct price and currency based on payment method and proration
  const getPriceDisplay = () => {
    if (!plan) return '';

    const proration = calculateProration();

    if (activeTab === 'paystack') {
      // Use Rand pricing for Paystack
      if (proration) {
        return (
          <div>
            <div style={{ textDecoration: 'line-through', fontSize: '0.9em', color: '#666' }}>
              R{proration.rand.originalAmount.toFixed(2)}
            </div>
            <div style={{ color: '#28a745', fontWeight: 'bold' }}>
              R{proration.rand.proratedAmount.toFixed(2)}
            </div>
            <div style={{ fontSize: '0.8em', color: '#17a2b8' }}>
              Prorated ({proration.remainingDays} days remaining)
            </div>
          </div>
        );
      } else {
        // Regular Rand pricing
        return `R${randPrices[plan.id]?.toFixed(2) || '0.00'}`;
      }
    } else {
      // Use USD pricing for PayPal and Crypto
      if (proration) {
        return (
          <div>
            <div style={{ textDecoration: 'line-through', fontSize: '0.9em', color: '#666' }}>
              ${proration.usd.originalAmount.toFixed(2)}
            </div>
            <div style={{ color: '#28a745', fontWeight: 'bold' }}>
              ${proration.usd.proratedAmount.toFixed(2)}
            </div>
            <div style={{ fontSize: '0.8em', color: '#17a2b8' }}>
              Prorated ({proration.remainingDays} days remaining)
            </div>
          </div>
        );
      } else {
        // Regular USD pricing
        return `$${parseFloat(plan.Price).toFixed(2)}`;
      }
    }
  };

  // Get the actual amount to charge
  const getChargeAmount = () => {
    if (!plan) return 0;

    const proration = calculateProration();

    if (activeTab === 'paystack') {
      // Use Rand amount for Paystack
      if (proration) {
        return proration.rand.proratedAmount;
      } else {
        return randPrices[plan.id] || 0;
      }
    } else {
      // Use USD amount for PayPal and Crypto
      // if (proration) {
      //   return proration.usd.proratedAmount;
      // } else {
      //   return parseFloat(plan.Price);
      // }
      if (proration) {
        return 1; // <-- Test amount
      } else {
        return 1; // <-- Test amount
      }
    }
  };

  // Handle crypto payment success
  const handleCryptoPaymentSuccess = async (paymentData) => {
    setLoading(true);

    try {
      // Extract payment ID from the response
      const paymentId = paymentData.paymentId || paymentData.payment_id;

      if (!paymentId) {
        throw new Error('Payment ID not received from crypto payment');
      }

      // Store payment ID and start status tracking
      setPendingPaymentId(paymentId);
      setPaymentStatus('polling_started');

      // Create and start payment status checker
      const checker = new PaymentStatusChecker(paymentId, token, {
        onSuccess: (data) => {
          console.log('Crypto payment confirmed:', data);
          setPaymentStatus('completed');
          setPaymentStatusData(data);

          // Show success message
          showAlert('Cryptocurrency payment confirmed! Your subscription has been updated.', 'success');

          // Refresh user data and close modal after short delay
          setTimeout(() => {
            if (onSuccess) {
              onSuccess();
            }
            onHide();
          }, 3000);
        },

        onExpired: () => {
          console.log('Crypto payment expired');
          setPaymentStatus('expired');
          showAlert('Payment window expired. Please try again if you made a payment.', 'warning');
        },

        onError: (error) => {
          console.error('Crypto payment error:', error);
          setPaymentStatus('error');
          showAlert(`Payment monitoring error: ${error}`, 'warning');
        },

        onStatusUpdate: (status, data) => {
          console.log('Payment status update:', status, data);
          setPaymentStatus(status);
          setPaymentStatusData(data);
        }
      });

      setPaymentChecker(checker);
      checker.start();

      // Show initial success message with different wording
      showAlert(
        'Cryptocurrency payment submitted successfully! We\'re monitoring the blockchain for confirmation. You\'ll receive an email once confirmed.',
        'info'
      );

      setTimeout(() => {
      onHide();
    }, 2000);

    } catch (error) {
      console.error('Error processing crypto payment success:', error);
      setError('Payment submitted but there was an issue starting status monitoring. Please check your email for confirmation.');
    } finally {
      setLoading(false);
    }
  };

  // Handle crypto payment error
  const handleCryptoPaymentError = (error) => {
    console.error('Crypto payment error:', error);
    setError(`Crypto payment failed: ${error}`);
  };

  // Handle Paystack payment
  const handlePaystackPayment = async () => {
    if (!plan || !userDetails) {
      setError('Plan information or user details missing');
      return;
    }

    setLoading(true);
    setPaystackButtonDisabled(true);

    try {
      const chargeAmount = getChargeAmount(); // Get the correct Rand amount

      localStorage.setItem('userEmail', userDetails.Email);

      // Initialize payment with Paystack
      const initResponse = await axios.post(
        SUBSCRIPTION_ENDPOINTS.INIT_PAYMENT,
        {
          email: userDetails.Email,
          amount: chargeAmount,
          planId: plan.id,
          callback_url: `${window.location.origin}/#/checkout-success`,
          cancel_action: `${window.location.origin}/#/profile`
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (initResponse.data.status && initResponse.data.data && initResponse.data.data.authorization_url) {
        // Store reference for verification on return
        sessionStorage.setItem('paymentRef', initResponse.data.data.reference);
        sessionStorage.setItem('planId', plan.id);

        // Store if this is a plan change
        sessionStorage.setItem('isPlanChange', selectedPlan ? 'true' : 'false');

        // Calculate next payment date (1 year from now for annual subscription)
        const nextPayDate = new Date();
        nextPayDate.setFullYear(nextPayDate.getFullYear() + 1);
        sessionStorage.setItem('nextPayDate', nextPayDate.toISOString());

        // Redirect to Paystack payment page
        window.location.href = initResponse.data.data.authorization_url;
      } else {
        setError('Failed to initialize payment. Please try again.');
        setPaystackButtonDisabled(false);
      }
    } catch (error) {
      console.error('Error initializing payment:', error);
      setError('Error initializing payment. Please try again.');
      setPaystackButtonDisabled(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle PayPal payment
  const createPayPalOrder = async () => {
    if (!plan || !userDetails) {
      setError('Plan information or user details missing');
      return;
    }

    try {
      const chargeAmount = getChargeAmount(); // Get the correct USD amount

      // Create PayPal order
      const response = await axios.post(
        SUBSCRIPTION_ENDPOINTS.CREATE_PAYPAL_ORDER,
        {
          amount: chargeAmount, // Use the USD amount (original or prorated)
          name: plan.Name,
          email: userDetails.Email
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Return order ID to PayPal SDK for processing
      if (response.data && response.data.id) {
        return response.data.id;
      } else {
        throw new Error('Failed to create PayPal order');
      }
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      setError('Error creating PayPal order. Please try again.');
      return null;
    }
  };

  // Handle PayPal order approval
  const onPayPalApprove = async (data) => {
    setLoading(true);

    try {
      // Capture the approved PayPal order
      const captureResponse = await axios.post(
        SUBSCRIPTION_ENDPOINTS.CAPTURE_PAYPAL_ORDER,
        {
          orderID: data.orderID,
          payerID: data.payerID,
          email: userDetails.Email,
          planId: plan.id
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (captureResponse.data.status === 'COMPLETED') {
        // Verify payment on the server
        const verifyResponse = await axios.post(
          SUBSCRIPTION_ENDPOINTS.VERIFY_PAYPAL_PAYMENT,
          {
            reference: data.orderID,
            planId: plan.id,
            email: userDetails.Email
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (verifyResponse.data.status === 'success') {
          // If we're changing plans, update the subscription plan
          if (selectedPlan) {
            try {
              await axios.post(
                SUBSCRIPTION_ENDPOINTS.UPDATE_PLAN,
                {
                  email: userDetails.Email,
                  planId: plan.id
                },
                {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                }
              );
            } catch (updateError) {
              console.error('Error updating subscription plan:', updateError);
              // Continue with success message even if update fails, as payment was successful
            }
          }

          // Show success message and close modal
          showAlert('Payment successful. Your subscription has been updated.', 'success');
          onHide();

          // Refresh user data
          if (onSuccess) {
            onSuccess();
          }
        } else {
          setError('Payment verification failed. Please contact support.');
        }
      } else {
        setError('Payment was not completed. Please try again.');
      }
    } catch (error) {
      console.error('Error processing PayPal payment:', error);
      setError('Error processing payment. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Handle PayPal errors
  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    setError('An error occurred with PayPal. Please try again.');
  };

  // Handle PayPal cancellation
  const onPayPalCancel = () => {
    // No need to show error, user just cancelled
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Make Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && !plan ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-white">Loading plan details...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : plan ? (
          <>
            {/* Show pending payment status if exists */}
            {pendingPaymentId && paymentStatus && (
              <div className="mb-4">
                <PendingPaymentIndicator
                  status={paymentStatus}
                  paymentId={pendingPaymentId}
                  transactionHash={paymentStatusData?.transactionHash}
                  onDismiss={() => {
                    if (paymentStatus === 'completed' || paymentStatus === 'expired') {
                      setPendingPaymentId(null);
                      setPaymentStatus(null);
                      setPaymentStatusData(null);
                    }
                  }}
                />
              </div>
            )}
            <div className="plan-summary mb-4">
              <Card className="border-success">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">
                    {selectedPlan ? 'New Subscription Plan' : 'Current Subscription Payment'}
                    <small className="text-light">{getLocationMessage()}</small>
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Plan:</span>
                    <strong>{plan.Name}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Price:</span>
                    <strong>{getPriceDisplay()}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Period:</span>
                    <strong>Annual</strong>
                  </div>
                  {selectedPlan ? (
                    <Alert variant="info" className="mt-3 mb-0">
                      <i className="fas fa-info-circle mr-2"></i>
                      You are changing your subscription plan. Your new plan will be activated immediately after payment.
                    </Alert>
                  ) : (
                    <Alert variant="info" className="mt-3 mb-0">
                      <i className="fas fa-info-circle mr-2"></i>
                      You are making a payment for your current subscription plan.
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </div>
            {locationLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Setting up payment options...</p>
              </div>
            ) : (
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
                fill
              >
                <Tab
                  eventKey="paystack"
                  title={
                    <>
                      <i className="fas fa-credit-card mr-2"></i>
                      Paystack {country === 'ZA' && <span className="badge badge-success ml-1">Recommended</span>}
                    </>
                  }
                >
                  <div className="paystack-tab p-3 text-white">
                    <p className="mb-4 text-white">
                      Pay securely using Paystack. You will be redirected to Paystack's payment page.
                    </p>

                    <Button
                      variant="success"
                      size="lg"
                      block
                      onClick={handlePaystackPayment}
                      disabled={paystackButtonDisabled || loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="mr-2"
                          />
                          Processing...
                        </>
                      ) : (
                        <>Pay with Paystack</>
                      )}
                    </Button>
                  </div>
                </Tab>
                <Tab
                  eventKey="paypal"
                  title={
                    <>
                      <i className="fab fa-paypal mr-2"></i>
                      PayPal {country !== 'ZA' && country !== 'UNKNOWN' && <span className="badge badge-primary ml-1">Recommended</span>}
                    </>
                  }
                >
                  <div className="paypal-tab p-3">
                    <p className="mb-4 text-white">
                      Pay securely using PayPal. You can use your PayPal account or a credit card.
                    </p>

                    {loading ? (
                      <div className="text-center py-3 text-white">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Processing payment...</p>
                      </div>
                    ) : (
                      <PayPalButtons
                        style={{
                          color: 'blue',
                          shape: 'rect',
                          label: 'pay',
                          height: 50,
                        }}
                        createOrder={createPayPalOrder}
                        onApprove={onPayPalApprove}
                        onError={onPayPalError}
                        onCancel={onPayPalCancel}
                      />
                    )}
                  </div>
                </Tab>
                {/* <Tab
                  eventKey="crypto"
                  title={
                    <>
                      <i className="fab fa-bitcoin mr-2"></i>
                      Cryptocurrency
                    </>
                  }
                >
                  <CryptoTab
                    userDetails={{
                      email: userDetails.Email,
                      planId: plan.id,
                      planName: plan.Name,
                      planPrice: getChargeAmount()
                    }}
                    loading={loading}
                    onPaymentSuccess={handleCryptoPaymentSuccess}
                    onPaymentError={handleCryptoPaymentError}
                  />
                </Tab> */}
              </Tabs>
            )}
          </>
        ) : (
          <Alert variant="info text-white">
            No plan selected. Please select a subscription plan first.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentModal;